import { useEffect, useSyncExternalStore } from 'react'
import {
  UserPlus,
  FileText,
  CalendarPlus,
  Receipt,
  CreditCard,
  Contact,
  MessageSquarePlus,
} from 'lucide-react'
import { CREATE_LABELS } from '@/lib/labels'
import type { CapabilityName } from '@/lib/permissions'
import type { NavItem } from './navItems'

/**
 * What a person can create from the phone, and where it goes. The create
 * actions live on the wheel navigation (WheelNav.tsx), behind its "Create"
 * bubble: this file is the shared list, the per-section lead, the permission
 * rules and the suppression hooks. (The standalone "+" that used to bubble
 * these out of the dock row was folded into the wheel.)
 */

export type CreateAction = {
  /** Stable key — also how a page puts its own object first. */
  kind: 'lead' | 'quote' | 'job' | 'payment' | 'invoice' | 'contact' | 'text'
  label: string
  to: string
  icon: React.ComponentType<{ className?: string }>
  /** Hidden for people who can't do it (UX-010). */
  capability: CapabilityName
  /** Tile colour in the wheel: the colour of where it leads (Text is Inbox
   *  sky, Job is Calendar blush, Contact is Contacts lavender), so each
   *  action is found by colour before its icon is read. */
  tone: NavItem['tone']
}

// Existing deep links — each one opens that page's own create flow.
export const CREATE_ACTIONS: CreateAction[] = [
  { kind: 'lead', tone: 'rose', label: CREATE_LABELS.lead, to: '/work?create=lead', icon: UserPlus, capability: 'createLead' },
  { kind: 'quote', tone: 'peach', label: CREATE_LABELS.quote, to: '/quotes/builder', icon: FileText, capability: 'createQuote' },
  { kind: 'job', tone: 'blush', label: CREATE_LABELS.job, to: '/work?create=job', icon: CalendarPlus, capability: 'scheduleJob' },
  // Its own icon: it shared the invoice's receipt (copy-a-51).
  { kind: 'payment', tone: 'mint', label: CREATE_LABELS.payment, to: '/payments', icon: CreditCard, capability: 'recordPayment' },
  { kind: 'invoice', tone: 'butter', label: CREATE_LABELS.invoice, to: '/work?create=invoice', icon: Receipt, capability: 'createInvoice' },
  { kind: 'contact', tone: 'lavender', label: CREATE_LABELS.contact, to: '/customers/people?new=1', icon: Contact, capability: 'createContact' },
  { kind: 'text', tone: 'sky', label: CREATE_LABELS.text, to: '/inbox?compose=1', icon: MessageSquarePlus, capability: 'sendMessage' },
]

/** The create actions this person may use, in menu order. */
export function allowedCreateActions(can: (c: CapabilityName) => boolean): CreateAction[] {
  return CREATE_ACTIONS.filter((a) => can(a.capability))
}

/** What each section mostly creates; that action leads the menu there. */
const SECTION_LEAD: Record<string, CreateAction['kind']> = {
  '/work': 'lead',
  '/calendar': 'job',
  '/inbox': 'text',
  '/customers': 'contact',
  '/contacts': 'contact',
  '/quotes': 'quote',
}

/**
 * Sections whose "+" only makes sense with one of these: on the Calendar the
 * "+" is for scheduling, so Crew who can't schedule a job or add an event
 * don't get it there (team-auth decision 5). Elsewhere the "+" stays (§21).
 */
const SECTION_NEEDS: Record<string, CapabilityName[]> = {
  '/calendar': ['scheduleJob', 'manageEvents'],
}

/**
 * The "+" is for creating work: a lead, quote, job, invoice, payment or text.
 * Crew (jobs, calendar, customer contact, start/complete, notes and photos)
 * can't create any of those, so they get no "+" on Home, Work, Calendar or
 * Inbox (UX-010). Contacts keeps it for them: adding a customer is in scope.
 */
const WORK_CREATE_KINDS: CreateAction['kind'][] = ['lead', 'quote', 'job', 'payment', 'invoice', 'text']

/** False when this section's "+" would offer nothing the person can do here. */
export function createMenuShownOn(pathname: string, can: (c: CapabilityName) => boolean): boolean {
  const section = '/' + (pathname.split('/')[1] ?? '')
  const createsWork = CREATE_ACTIONS.some((a) => WORK_CREATE_KINDS.includes(a.kind) && can(a.capability))
  // Contact-only roles keep the "+" on Contacts (the phone's only way to add one there).
  if (!createsWork) return SECTION_LEAD[section] === 'contact' && can('createContact')
  const needs = SECTION_NEEDS[section]
  return !needs || needs.some(can)
}

/**
 * Same list everywhere (so muscle memory holds), but the current page's object
 * comes first — closest to the thumb.
 */
export function orderCreateActions(actions: CreateAction[], pathname: string): CreateAction[] {
  const lead = SECTION_LEAD['/' + (pathname.split('/')[1] ?? '')]
  if (!lead) return actions
  const first = actions.find((a) => a.kind === lead)
  return first ? [first, ...actions.filter((a) => a !== first)] : actions
}


// ── Suppression ─────────────────────────────────────────────────────────────
// A page that is itself a create flow (Take payment) mounts
// <SuppressCreateMenu/> so the "+" stays out of its way.
let suppressCount = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }
export function useCreateMenuSuppressed() {
  return useSyncExternalStore(subscribe, () => suppressCount > 0, () => false)
}

/** For a page that is itself a create flow: hides the "+" while mounted. */
export function SuppressCreateMenu() {
  useEffect(() => {
    suppressCount++; emit()
    return () => { suppressCount--; emit() }
  }, [])
  return null
}
